import "./StepDurationBar.css";

/**
 * Phase 18 WU-18.F2 — horizontal stacked bar showing step durations as a
 * proportion of the total. Used on both the DocumentTrailPage (single doc)
 * and BatchTrailPage (aggregate across docs).
 *
 * When `slaTargetMs` sits within `totalDurationMs`, a dashed vertical line
 * is drawn at the target position so the reader can see how far past the
 * SLA the actual duration ran.
 */
export interface StepDurationBarProps {
  stepDurations: Record<string, number>;
  slaTargetMs?: number | null;
  totalDurationMs: number;
}

// Segment colors cycle through the shared analytics palette defined in
// globals.css (--chart-series-1..6). Reading via CSS variables keeps the
// legend theme-aware.
const PALETTE = [
  "var(--chart-series-1)",
  "var(--chart-series-5)",
  "var(--chart-series-2)",
  "var(--chart-series-3)",
  "var(--chart-series-4)",
  "var(--chart-series-6)",
];

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "—";
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m}m ${s}s`;
}

export function StepDurationBar({
  stepDurations,
  slaTargetMs,
  totalDurationMs,
}: StepDurationBarProps) {
  const entries = Object.entries(stepDurations).filter(([, v]) => v > 0);

  if (totalDurationMs <= 0 || entries.length === 0) {
    return (
      <div className="step-duration__empty" data-testid="step-duration-bar-empty">
        No step durations recorded.
      </div>
    );
  }

  const showSla =
    slaTargetMs != null &&
    slaTargetMs > 0 &&
    slaTargetMs < totalDurationMs;
  const slaLeftPct = showSla ? (slaTargetMs / totalDurationMs) * 100 : 0;

  // Use the sum of segments as the denominator, clamped to totalDurationMs
  // so the bar never overflows. If segments sum to less than total, the
  // remaining width is a "gap" segment (idle / between-step time).
  const segSum = entries.reduce((a, [, v]) => a + v, 0);
  const denom = Math.max(segSum, totalDurationMs);
  const gapMs = Math.max(0, totalDurationMs - segSum);

  return (
    <div className="step-duration" data-testid="step-duration-bar">
      <div className="step-duration__bar" role="img" aria-label="Step durations">
        {entries.map(([stepId, ms], i) => {
          const widthPct = (ms / denom) * 100;
          const color = PALETTE[i % PALETTE.length];
          return (
            <div
              key={stepId}
              className="step-duration__segment"
              data-testid={`step-duration-segment-${stepId}`}
              style={{ width: `${widthPct}%`, background: color }}
              title={`${stepId} — ${formatDuration(ms)}`}
            >
              <span className="step-duration__segment-label">{stepId}</span>
            </div>
          );
        })}
        {gapMs > 0 && (
          <div
            className="step-duration__segment step-duration__segment--gap"
            data-testid="step-duration-segment-gap"
            style={{ width: `${(gapMs / denom) * 100}%` }}
            title={`idle / gap — ${formatDuration(gapMs)}`}
          />
        )}
        {showSla && (
          <div
            className="step-duration__sla-marker"
            data-testid="step-duration-sla-marker"
            style={{ left: `${slaLeftPct}%` }}
            aria-label={`SLA target ${formatDuration(slaTargetMs)}`}
          />
        )}
      </div>
      <div className="step-duration__legend">
        {entries.map(([stepId, ms], i) => (
          <div className="step-duration__legend-item" key={stepId}>
            <span
              className="step-duration__legend-swatch"
              style={{ background: PALETTE[i % PALETTE.length] }}
              aria-hidden="true"
            />
            <span className="step-duration__legend-name">{stepId}</span>
            <span className="step-duration__legend-value">{formatDuration(ms)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
